import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { faBan, faLink, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import {
  Button,
  IconButton,
  Badge,
  Card,
  Input,
  Tabs,
  Switch,
  Checkbox,
  SidePanel,
  KebabMenu,
  PulseDot,
  Icon,
  useToast,
} from '../components/ui';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type AvailabilityRule = {
  id?: string;
  day_of_week: number;
  start_minute: number;
  end_minute: number;
};

type DateOverride = {
  id?: string;
  override_date: string;
  start_minute: number | null;
  end_minute: number | null;
};

type EventType = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_minutes: number;
  max_advance_days: number;
  slot_increment_minutes: number;
  admin_timezone: string;
  is_active: boolean;
  // Reminder offsets in minutes. null disables that reminder slot.
  // Defaults: 1440 (24h) and 60 (1h).
  reminder_offset_minutes_1: number | null;
  reminder_offset_minutes_2: number | null;
  created_at: string;
  updated_at: string;
  availability_rules: AvailabilityRule[];
  date_overrides: DateOverride[];
};

type GoogleAccount = {
  email: string;
  calendar_id: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
};

// Health read model from booking_admin_calendar_health. Exists because the
// Integrations card used to render a hardcoded green "Connected" badge — on
// 2026-08-13 the OAuth grant died and the badge stayed green for four days
// while two bookings landed with no calendar event.
type CalendarHealth = {
  accounts: Array<{
    email: string;
    calendar_id: string;
    expires_at: string;
    updated_at: string;
    created_at: string;
    // updated_at only moves when a token refresh succeeds, so its age is the
    // honest signal — expires_at just says when the last one would lapse.
    stale: boolean;
    expired: boolean;
  }>;
  open_alerts: Array<{
    key: string;
    first_seen_at: string;
    last_alerted_at: string | null;
    alert_count: number;
    detail: string | null;
  }>;
  bookings_missing_events: number;
  busy_intervals: number;
  checked_at: string;
};

// An open alert means the watchdog saw a REAL Google API call fail. That is
// authoritative in a way updated_at is not: updated_at records when a refresh
// last succeeded, so a grant revoked minutes after a good refresh still looks
// fresh for hours. On 2026-08-17 a grant died ~10 min after reconnecting and
// this badge read "Connected" the whole time, because it only consulted
// updated_at while the failure sat unread in open_alerts.
function integrationAlert(h: CalendarHealth | null) {
  if (!h) return null;
  return (
    h.open_alerts.find(
      (a) => a.key.startsWith('google_token:') || a.key === 'google_busy_sync'
    ) || null
  );
}

function healthVerdict(h: CalendarHealth | null): {
  tone: 'up' | 'warn' | 'down';
  label: string;
  detail: string | null;
} {
  if (!h) return { tone: 'up', label: 'Connected', detail: null };
  if (h.accounts.length === 0) {
    return { tone: 'down', label: 'Not connected', detail: 'No calendar is connected.' };
  }
  const alert = integrationAlert(h);
  if (alert) {
    const since = fmtDate(alert.first_seen_at);
    const why = (alert.detail || '').slice(0, 160);
    return {
      tone: 'down',
      label: 'Reconnect required',
      detail: `Google is rejecting our credentials (failing since ${since})${why ? ` — ${why}` : ''}`,
    };
  }
  const broken = h.accounts.find((a) => a.stale);
  if (broken) {
    return {
      tone: 'down',
      label: 'Reconnect required',
      detail: `Token last refreshed ${fmtDate(broken.updated_at)} — refreshes have been failing since.`,
    };
  }
  if (h.bookings_missing_events > 0) {
    return {
      tone: 'warn',
      label: 'Syncing',
      detail: `${h.bookings_missing_events} upcoming booking${h.bookings_missing_events === 1 ? '' : 's'} still missing a calendar event.`,
    };
  }
  return { tone: 'up', label: 'Connected', detail: null };
}

type Booking = {
  id: string;
  event_type_id: string;
  event_name: string;
  event_slug: string;
  start_at: string;
  end_at: string;
  invitee_name: string;
  invitee_email: string;
  invitee_phone: string | null;
  invitee_company: string | null;
  invitee_notes: string | null;
  status: 'confirmed' | 'cancelled';
  google_event_id: string | null;
  google_meet_url: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  created_at: string;
};

// booking.blocked_invitee: people the booking page refuses (book and
// reschedule both). Email is stored lowercased; Gmail lookalikes (dots, +tags)
// match too. Phone is digits only and matches on the last 10.
type BlockedInvitee = {
  id: string;
  email: string | null;
  phone_digits: string | null;
  reason: string | null;
  created_at: string;
};

// booking.blocked_ip: the IP a blocked person tried from, refused for 30 days
// whatever email it uses next. IPv6 entries are a /64.
type BlockedIp = {
  id: string;
  ip: string;
  source_email: string | null;
  country: string | null;
  city: string | null;
  attempts: number;
  last_attempt_email: string | null;
  last_attempt_at: string;
  created_at: string;
  expires_at: string;
};

// booking.ip_allow: our own networks, never IP-blocked. my_ip is the address
// the Supabase API saw this browser call from.
type IpAllow = {
  my_ip: string | null;
  my_ip_allowed: boolean;
  rows: Array<{ id: string; ip: string; label: string | null; created_at: string; is_mine: boolean }>;
};

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    return String((e as { message?: unknown }).message);
  }
  return String(e);
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function hhmmToMinutes(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function isTodayLocal(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return (
    d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate()
  );
}

type TabKey = 'upcoming' | 'past' | 'cancelled' | 'blocked' | 'availability' | 'integrations';

const TAB_DEFS: Array<{ label: string; value: TabKey }> = [
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Past', value: 'past' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Availability', value: 'availability' },
  { label: 'Integrations', value: 'integrations' },
];

export default function Bookings() {
  const { user, isUserLoaded } = useUser();
  const toast = useToast();

  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccount[]>([]);
  const [health, setHealth] = useState<CalendarHealth | null>(null);
  const [blocked, setBlocked] = useState<BlockedInvitee[] | null>(null);
  const [blockedIps, setBlockedIps] = useState<BlockedIp[] | null>(null);
  const [ipAllow, setIpAllow] = useState<IpAllow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('upcoming');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const tabStripRef = useRef<HTMLDivElement>(null);

  const role = normalizeRole(user?.role);
  const allowed = role === 'admin' || role === 'developer' || role === 'agency_owner';

  // Phones scroll the tab strip; keep the selected tab (and its underline) on screen
  useEffect(() => {
    const strip = tabStripRef.current;
    const label = TAB_DEFS.find((t) => t.value === tab)?.label;
    const btn = [...(strip?.querySelectorAll('button') ?? [])].find((b) => b.textContent === label);
    if (!strip || !btn || strip.scrollWidth <= strip.clientWidth) return;
    strip.scrollTo({ left: btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  }, [tab]);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [stateRes, bookingsRes, googleRes, healthRes, blockedRes, blockedIpsRes, ipAllowRes] = await Promise.all([
        supabase.rpc('booking_admin_state'),
        supabase.rpc('booking_admin_list_bookings', {
          p_window_start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          p_window_end: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
          p_status: undefined,
        }),
        supabase.rpc('booking_admin_google_status'),
        supabase.rpc('booking_admin_calendar_health'),
        supabase.rpc('booking_admin_list_blocked'),
        supabase.rpc('booking_admin_list_blocked_ips'),
        supabase.rpc('booking_admin_list_ip_allow'),
      ]);
      if (stateRes.error) throw stateRes.error;
      if (bookingsRes.error) throw bookingsRes.error;
      if (googleRes.error) throw googleRes.error;
      setEventTypes((stateRes.data as EventType[]) || []);
      setBookings((bookingsRes.data as Booking[]) || []);
      setGoogleAccounts((googleRes.data as GoogleAccount[]) || []);
      // Soft-fail: this RPC ships in the 20260817 migration, and the page
      // must still load if cockpit deploys ahead of the database. A null
      // health just falls back to the old, less informative rendering.
      if (healthRes.error) {
        console.warn('booking admin: calendar health unavailable', healthRes.error);
        setHealth(null);
      } else {
        setHealth((healthRes.data as CalendarHealth) || null);
      }
      // Soft-fail like health: the Blocked tab says it couldn't load rather
      // than taking the whole screen down.
      if (blockedRes.error) {
        console.warn('booking admin: blocked list unavailable', blockedRes.error);
        setBlocked(null);
      } else {
        setBlocked((blockedRes.data as BlockedInvitee[]) || []);
      }
      if (blockedIpsRes.error || ipAllowRes.error) {
        console.warn('booking admin: blocked IPs unavailable', blockedIpsRes.error || ipAllowRes.error);
      }
      setBlockedIps(blockedIpsRes.error ? null : (blockedIpsRes.data as BlockedIp[]) || []);
      setIpAllow(ipAllowRes.error ? null : (ipAllowRes.data as IpAllow) || null);
    } catch (e: unknown) {
      console.error('booking admin: load failed', e);
      setError(errMsg(e) || 'Failed to load.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !allowed) return;
    void loadAll();
  }, [user, allowed, loadAll]);

  // OAuth callback returns to /bookings?google_cal=... — surface a banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google_cal');
    if (!flag) return;
    if (flag === 'connected') {
      const email = params.get('email') || '';
      setBanner({ kind: 'ok', msg: `Connected ${email}.` });
      setTab('integrations');
    } else if (flag === 'replayed') {
      // A single-use authorization code was submitted twice (refresh, back
      // button, stale tab, link prefetch). The first submission is what
      // counts, and it succeeded — so this is reassurance, not an error.
      setBanner({
        kind: 'ok',
        msg: 'That connect link had already been used. Your calendar status below is current.',
      });
      setTab('integrations');
    } else if (flag === 'error') {
      setBanner({
        kind: 'err',
        msg: 'Google Calendar connect failed: ' + (params.get('reason') || 'unknown'),
      });
      setTab('integrations');
    }
    params.delete('google_cal');
    params.delete('email');
    params.delete('reason');
    const next = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', next);
  }, []);

  const closeCancel = () => {
    if (isCancelling) return;
    setCancelTarget(null);
    setCancelReason('');
  };

  async function blockInvitee(b: Booking) {
    const who = b.invitee_phone ? `${b.invitee_email} and ${b.invitee_phone}` : b.invitee_email;
    if (!confirm(`Block ${b.invitee_name} (${who}) from booking or rescheduling?`)) return;
    const { error: blockError } = await supabase.rpc('booking_admin_add_blocked', {
      p_email: b.invitee_email,
      p_phone: b.invitee_phone ?? undefined,
      p_reason: `Blocked from the ${fmtDate(b.start_at)} booking`,
    });
    if (blockError) {
      toast.error('Not blocked', blockError.message);
      return;
    }
    toast.success(`${b.invitee_name} is blocked`);
    await loadAll();
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    setIsCancelling(true);
    const { error: cancelError } = await supabase.rpc('booking_admin_cancel_booking', {
      p_id: cancelTarget.id,
      p_reason: cancelReason.trim() || undefined,
    });
    setIsCancelling(false);
    if (cancelError) {
      toast.error('Booking not cancelled', cancelError.message);
      return;
    }
    setCancelTarget(null);
    setCancelReason('');
    await loadAll();
  }

  if (!isUserLoaded) {
    return (
      <div className="min-h-screen">
        <Sidebar />
      </div>
    );
  }
  if (!allowed) return <Navigate to="/" replace />;

  const now = Date.now();
  const byStartAsc = (a: Booking, b: Booking) =>
    new Date(a.start_at).getTime() - new Date(b.start_at).getTime();
  const upcomingBookings = bookings
    .filter((b) => b.status === 'confirmed' && new Date(b.start_at).getTime() >= now)
    .sort(byStartAsc);
  const pastBookings = bookings
    .filter((b) => b.status === 'confirmed' && new Date(b.start_at).getTime() < now)
    .sort((a, b) => byStartAsc(b, a));
  const cancelledBookings = bookings
    .filter((b) => b.status === 'cancelled')
    .sort((a, b) => byStartAsc(b, a));

  const activeTabLabel = TAB_DEFS.find((t) => t.value === tab)?.label ?? 'Upcoming';
  const listForTab =
    tab === 'upcoming' ? upcomingBookings : tab === 'past' ? pastBookings : cancelledBookings;

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader title="Bookings" count={upcomingBookings.length} />

        {/* Tabs. Six labels don't fit a phone: the strip scrolls sideways
            on its own instead of widening (and zooming out) the whole page. */}
        <div ref={tabStripRef} className="overflow-x-auto scrollbar-hidden" style={{ marginBottom: 'var(--space-5)' }}>
          <Tabs
            style={{ width: 'max-content', minWidth: '100%' }}
            tabs={TAB_DEFS.map((t) => t.label)}
            active={activeTabLabel}
            onChange={(label) => {
              const def = TAB_DEFS.find((t) => t.label === label);
              if (def) setTab(def.value);
            }}
          />
        </div>

        {error && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              color: 'var(--status-down)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}
        {banner && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              background: banner.kind === 'ok' ? 'var(--status-up-soft)' : 'var(--status-down-soft)',
              color: banner.kind === 'ok' ? 'var(--status-up)' : 'var(--status-down)',
              fontSize: 'var(--text-sm)',
            }}
          >
            <span>{banner.msg}</span>
            <IconButton icon="x" size="sm" label="Dismiss" onClick={() => setBanner(null)} />
          </div>
        )}

        {/* Integration health. Deliberately not dismissible and rendered on
            every tab: the failure mode this exists for is one you don't go
            looking for, because nothing appears wrong until a call is missing
            from your calendar. */}
        {!isLoading && healthVerdict(health).tone !== 'up' && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              background:
                healthVerdict(health).tone === 'down'
                  ? 'var(--status-down-soft)'
                  : 'var(--status-warn-soft)',
              color:
                healthVerdict(health).tone === 'down'
                  ? 'var(--status-down)'
                  : 'var(--status-warn)',
              fontSize: 'var(--text-sm)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <Icon name="circle-alert" size={16} />
              <span>
                <strong style={{ fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>
                  Google Calendar: {healthVerdict(health).label}.
                </strong>{' '}
                {healthVerdict(health).detail}
                {health && health.bookings_missing_events > 0 && healthVerdict(health).tone === 'down'
                  ? ` ${health.bookings_missing_events} upcoming booking${health.bookings_missing_events === 1 ? '' : 's'} affected.`
                  : ''}
              </span>
            </div>
            {healthVerdict(health).tone === 'down' ? (
              <ConnectLink label="Reconnect" icon="refresh-cw" />
            ) : null}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-7 h-7 rounded-full animate-spin"
              style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : tab === 'integrations' ? (
          <IntegrationsPanel
            accounts={googleAccounts}
            health={health}
            onDisconnect={async (email) => {
              if (!confirm(`Disconnect ${email}?`)) return;
              const { error: disconnectError } = await supabase.rpc(
                'booking_admin_disconnect_google',
                { p_email: email }
              );
              if (disconnectError) {
                toast.error('Disconnect failed', disconnectError.message);
                return;
              }
              await loadAll();
            }}
          />
        ) : tab === 'blocked' ? (
          <BlockedPanel blocked={blocked} blockedIps={blockedIps} ipAllow={ipAllow} onReload={loadAll} />
        ) : tab === 'availability' ? (
          <AvailabilityPanel
            eventTypes={eventTypes}
            savingId={savingId}
            onSaveStart={(id) => setSavingId(id)}
            onSaveEnd={() => setSavingId(null)}
            onReload={loadAll}
          />
        ) : (
          <BookingsList
            bookings={listForTab}
            emptyLabel={`No ${activeTabLabel.toLowerCase()} bookings`}
            onCancel={(b) => {
              setCancelTarget(b);
              setCancelReason('');
            }}
            onBlock={(b) => void blockInvitee(b)}
          />
        )}
      </main>

      {/* Cancel booking panel */}
      <SidePanel
        open={!!cancelTarget}
        onClose={closeCancel}
        width={400}
        eyebrow="Booking"
        title="Cancel Booking"
        footer={
          <>
            <Button variant="danger" disabled={isCancelling} onClick={() => void confirmCancel()}>
              {isCancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
            <Button variant="ghost" disabled={isCancelling} onClick={closeCancel}>
              Keep It
            </Button>
          </>
        }
      >
        {cancelTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              Cancels <span style={{ color: 'var(--text-body)' }}>{cancelTarget.event_name}</span>{' '}
              with {cancelTarget.invitee_name} —{' '}
              <span className="t-num">
                {fmtDate(cancelTarget.start_at)}, {fmtTime(cancelTarget.start_at)}
              </span>
              . The slot reopens on your booking page.
            </p>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
                Reason (optional)
              </div>
              <textarea
                className="ck-textarea"
                rows={3}
                placeholder="Rescheduling for next week..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function BookingRow({
  b,
  onCancel,
  onBlock,
}: {
  b: Booking;
  onCancel: (b: Booking) => void;
  onBlock: (b: Booking) => void;
}) {
  const start = new Date(b.start_at);
  const today = isTodayLocal(b.start_at);
  const mins = Math.max(
    0,
    Math.round((new Date(b.end_at).getTime() - start.getTime()) / 60000)
  );
  const dow = start.toLocaleDateString('en-US', { weekday: 'short' });
  const month = start.toLocaleDateString('en-US', { month: 'short' });

  const detailBits: React.ReactNode[] = [b.invitee_name];
  if (b.invitee_company) detailBits.push(b.invitee_company);

  return (
    // Phones: the actions drop onto their own line under the details, so the
    // body gets the full width instead of a one-word column.
    <div
      className="hover-lift flex flex-wrap md:flex-nowrap items-start md:items-center gap-x-3 gap-y-2.5 md:gap-[16px] px-3.5 py-3 md:px-[18px] md:py-3.5"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
      }}
    >
      {/* Date block */}
      <div style={{ width: '52px', textAlign: 'center', flexShrink: 0 }}>
        <div
          className="t-eyebrow"
          style={{ color: today ? 'var(--accent-text)' : 'var(--text-faint)' }}
        >
          {dow}
        </div>
        <div
          className="t-num"
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-body)',
            lineHeight: 1.1,
          }}
        >
          {start.getDate()}
        </div>
        <div className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
          {month}
        </div>
      </div>

      <div
        style={{ width: '1px', alignSelf: 'stretch', background: 'var(--border-hairline)', flexShrink: 0 }}
      />

      {/* Body */}
      <div style={{ flex: '1 1 0', minWidth: 0, overflowWrap: 'anywhere' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px', marginBottom: '3px' }}>
          <span
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            {b.event_name}
          </span>
          {today && b.status === 'confirmed' ? <PulseDot tone="info" title="Today" /> : null}
          {/* The symptom of a broken calendar write, shown where you'd
              actually notice it: on the booking itself. Only meaningful for
              calls that haven't happened yet — backfilling a past one helps
              nobody, so we don't nag about it. */}
          {b.status === 'confirmed' && !b.google_event_id && start.getTime() >= Date.now() ? (
            <Badge tone="down" dot>
              Not on calendar
            </Badge>
          ) : null}
        </div>
        <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {fmtTime(b.start_at)} – {fmtTime(b.end_at)} · {mins} min
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
          {detailBits.map((bit, i) => (
            <span key={i}>
              {i > 0 ? ' · ' : ''}
              {bit}
            </span>
          ))}
          {' · '}
          <a
            href={`mailto:${b.invitee_email}`}
            className="t-num hover-underline"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {b.invitee_email}
          </a>
          {b.invitee_phone ? (
            <>
              {' · '}
              <span className="t-num">{b.invitee_phone}</span>
            </>
          ) : null}
        </div>
        {b.invitee_notes ? (
          <div
            style={{
              marginTop: '6px',
              padding: '6px 10px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--surface-raised)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {b.invitee_notes}
          </div>
        ) : null}
        {b.status === 'cancelled' && b.cancellation_reason ? (
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
              marginTop: '4px',
              fontStyle: 'italic',
            }}
          >
            Reason: {b.cancellation_reason}
          </div>
        ) : null}
      </div>

      {/* Right rail */}
      {b.status === 'cancelled' ? (
        <Badge tone="down">Cancelled</Badge>
      ) : (
        <div
          className="basis-full md:basis-auto justify-end"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}
        >
          {b.google_meet_url ? (
            <Button
              size="sm"
              variant="secondary"
              iconTrailing="arrow-up-right"
              nudge
              data-dir="up"
              onClick={() => window.open(b.google_meet_url!, '_blank', 'noopener,noreferrer')}
            >
              Join Meet
            </Button>
          ) : null}
          <KebabMenu
            label="Booking actions"
            actions={[
              b.google_meet_url
                ? {
                    label: 'Copy Meet Link',
                    icon: faLink,
                    onClick: () => {
                      void navigator.clipboard?.writeText(b.google_meet_url!).catch(() => {});
                    },
                  }
                : null,
              { label: 'Cancel Booking', icon: faBan, danger: true, onClick: () => onCancel(b) },
              { label: 'Block This Person', icon: faUserSlash, danger: true, onClick: () => onBlock(b) },
            ]}
          />
        </div>
      )}
    </div>
  );
}

function BookingsList({
  bookings,
  emptyLabel,
  onCancel,
  onBlock,
}: {
  bookings: Booking[];
  emptyLabel: string;
  onCancel: (b: Booking) => void;
  onBlock: (b: Booking) => void;
}) {
  if (bookings.length === 0) {
    return (
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          padding: '56px 0',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <Icon name="calendar-days" size={24} style={{ color: 'var(--text-faint)' }} />
        <p style={{ margin: '12px 0 0', fontSize: 'var(--text-base)' }}>{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div
      className="ck-stagger"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: '880px' }}
    >
      {bookings.map((b) => (
        <BookingRow key={b.id} b={b} onCancel={onCancel} onBlock={onBlock} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function IntegrationsPanel({
  accounts,
  health,
  onDisconnect,
}: {
  accounts: GoogleAccount[];
  health: CalendarHealth | null;
  onDisconnect: (email: string) => void;
}) {
  const healthFor = (email: string) => health?.accounts.find((a) => a.email === email) || null;
  const alert = integrationAlert(health);
  return (
    <div
      className="ck-stagger"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: '640px' }}
    >
      {accounts.length === 0 ? (
        <Card eyebrow="Google Calendar" title="Connect Your Calendar">
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              lineHeight: 1.5,
            }}
          >
            When connected, Bullfinch creates Google Meet events for new bookings and blocks slots
            that conflict with existing calendar entries.
          </p>
          <div style={{ marginTop: '14px' }}>
            <ConnectLink label="Connect" icon="calendar-days" />
          </div>
        </Card>
      ) : (
        accounts.map((a) => {
          const h = healthFor(a.email);
          // Open watchdog alert first (a real API call failed), then a stale
          // updated_at as backstop. Checking only the latter is what let this
          // card show a green "Connected" over a revoked grant.
          const dead = h
            ? h.stale || alert !== null
            : new Date(a.expires_at).getTime() < Date.now();
          return (
            <Card
              key={a.email}
              eyebrow="Google Calendar"
              title={<span className="t-num">{a.email}</span>}
              actions={
                <Badge tone={dead ? 'down' : 'up'} dot pulse={dead}>
                  {dead ? 'Reconnect required' : 'Connected'}
                </Badge>
              }
            >
              {dead && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--status-down-soft)',
                    color: 'var(--status-down)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: 1.5,
                  }}
                >
                  {alert ? (
                    <>
                      Google is rejecting our credentials — failing since{' '}
                      <span className="t-num">{fmtDate(alert.first_seen_at)}</span> (
                      {alert.alert_count} alert{alert.alert_count === 1 ? '' : 's'} sent).
                    </>
                  ) : (
                    <>
                      Token refreshes stopped working{' '}
                      <span className="t-num">{fmtDate(h?.updated_at || a.updated_at)}</span>.
                    </>
                  )}{' '}
                  Until you reconnect, new bookings get no calendar event and no Meet link, and your
                  existing events stop blocking booking slots.
                </div>
              )}
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                Bookings write to{' '}
                <span className="t-num" style={{ color: 'var(--text-body)' }}>
                  {a.calendar_id}
                </span>{' '}
                · connected {fmtDate(a.created_at)}
              </div>
              <div
                className="t-num"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '4px' }}
              >
                Last successful refresh {fmtDate(h?.updated_at || a.updated_at)},{' '}
                {fmtTime(h?.updated_at || a.updated_at)}
                {health ? ` · ${health.busy_intervals} busy intervals cached` : ''}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <ConnectLink label="Reconnect" icon="refresh-cw" />
                <Button size="sm" variant="ghost" onClick={() => onDisconnect(a.email)}>
                  Disconnect
                </Button>
              </div>
            </Card>
          );
        })
      )}

      <Card eyebrow="Reminders" title="Test Reminder Email">
        <ReminderTestRow />
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Who the booking page refuses. The refusal itself lives in the database (a
// trigger on booking.booking), so adding or removing a row here takes effect
// on the next booking attempt with no deploy.
function BlockedPanel({
  blocked,
  blockedIps,
  ipAllow,
  onReload,
}: {
  blocked: BlockedInvitee[] | null;
  blockedIps: BlockedIp[] | null;
  ipAllow: IpAllow | null;
  onReload: () => Promise<void>;
}) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function add() {
    if (!email.trim() && !phone.trim()) {
      toast.error('Enter an email or a phone number');
      return;
    }
    setAdding(true);
    const { error } = await supabase.rpc('booking_admin_add_blocked', {
      p_email: email.trim() || undefined,
      p_phone: phone.trim() || undefined,
      p_reason: reason.trim() || undefined,
    });
    setAdding(false);
    if (error) {
      const msg = error.message.includes('invalid_email')
        ? "That email doesn't look right."
        : error.message.includes('invalid_phone')
          ? 'A phone number needs 10 to 15 digits.'
          : error.message;
      toast.error('Not blocked', msg);
      return;
    }
    toast.success(`Blocked ${email.trim() || phone.trim()}`);
    setEmail('');
    setPhone('');
    setReason('');
    await onReload();
  }

  async function remove(row: BlockedInvitee) {
    const who = row.email || row.phone_digits;
    if (!confirm(`Unblock ${who}? They'll be able to book again.`)) return;
    setRemovingId(row.id);
    const { error } = await supabase.rpc('booking_admin_remove_blocked', { p_id: row.id });
    setRemovingId(null);
    if (error) {
      toast.error('Not unblocked', error.message);
      return;
    }
    toast.success(`Unblocked ${who}`);
    await onReload();
  }

  async function unblockIp(row: BlockedIp) {
    if (!confirm(`Unblock ${row.ip}? Anyone on it can book again (blocked emails still can't).`)) return;
    setRemovingId(row.id);
    const { error } = await supabase.rpc('booking_admin_remove_blocked_ip', { p_id: row.id });
    setRemovingId(null);
    if (error) {
      toast.error('Not unblocked', error.message);
      return;
    }
    toast.success(`Unblocked ${row.ip}`);
    await onReload();
  }

  async function allowMyIp() {
    const label = prompt('Label for this network (e.g. "Kyle home")', '');
    if (label === null) return;
    setRemovingId('allow-me');
    const { data, error } = await supabase.rpc('booking_admin_allow_my_ip', { p_label: label || undefined });
    setRemovingId(null);
    if (error) {
      toast.error('Not added', error.message);
      return;
    }
    toast.success(`${(data as { ip?: string } | null)?.ip ?? 'This network'} will never be IP-blocked`);
    await onReload();
  }

  async function removeAllow(row: IpAllow['rows'][number]) {
    if (!confirm(`Remove ${row.ip}${row.label ? ` (${row.label})` : ''} from never-blocked networks?`)) return;
    setRemovingId(row.id);
    const { error } = await supabase.rpc('booking_admin_remove_ip_allow', { p_id: row.id });
    setRemovingId(null);
    if (error) {
      toast.error('Not removed', error.message);
      return;
    }
    await onReload();
  }

  const rowStyle = (i: number): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)',
  });
  const subStyle: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' };

  return (
    <div
      className="ck-stagger"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: '640px' }}
    >
      <Card eyebrow="Booking page" title="Block Someone">
        <p
          style={{
            margin: '0 0 14px',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-muted)',
            lineHeight: 1.5,
          }}
        >
          They can still pick a time, but booking or rescheduling stops at the last step with
          "We're not able to book this call online." Nothing tells them they're blocked, and you get
          no alert. Gmail addresses match with or without dots and +tags. When they try, the IP they
          tried from is blocked for 30 days too, so a new email from there gets the same answer.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          <div className="flex flex-col md:flex-row" style={{ gap: '10px' }}>
            <Input
              label="Email"
              type="email"
              fieldSize="sm"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Phone (optional)"
              type="tel"
              fieldSize="sm"
              placeholder="(555) 555-0100"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <Input
            label="Reason (optional)"
            fieldSize="sm"
            placeholder="No-show, spam..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div>
            <Button size="sm" variant="danger" type="submit" disabled={adding}>
              {adding ? 'Blocking...' : 'Block'}
            </Button>
          </div>
        </form>
      </Card>

      {blocked === null ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--status-down)' }}>
          Couldn't load the blocked list.
        </div>
      ) : blocked.length === 0 ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Nobody is blocked.</div>
      ) : (
        <Card eyebrow="Blocked" title={`${blocked.length} ${blocked.length === 1 ? 'person' : 'people'}`}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {blocked.map((row, i) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 0',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)',
                }}
              >
                <div style={{ flex: '1 1 0', minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {[row.email, row.phone_digits].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                    {row.reason ? `${row.reason} · ` : ''}
                    <span className="t-num">since {fmtDate(row.created_at)}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={removingId === row.id}
                  onClick={() => void remove(row)}
                >
                  {removingId === row.id ? 'Removing...' : 'Unblock'}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {blockedIps === null ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--status-down)' }}>
          Couldn't load the blocked IPs.
        </div>
      ) : (
        <Card
          eyebrow="Blocked IPs"
          title={blockedIps.length === 0 ? 'None right now' : `${blockedIps.length} blocked`}
        >
          {blockedIps.length === 0 ? (
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              When someone on the list above tries to book, the IP they tried from shows up here and is
              refused for 30 days, whatever email it uses.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {blockedIps.map((row, i) => {
                const place = [row.city, row.country].filter(Boolean).join(', ');
                const tried =
                  row.last_attempt_email && row.last_attempt_email !== row.source_email
                    ? ` · last tried as ${row.last_attempt_email}`
                    : '';
                return (
                  <div key={row.id} style={rowStyle(i)}>
                    <div style={{ flex: '1 1 0', minWidth: 0, overflowWrap: 'anywhere' }}>
                      <div className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                        {row.ip}
                        {place ? (
                          <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-ui)' }}> · {place}</span>
                        ) : null}
                      </div>
                      <div style={subStyle}>
                        Set off by {row.source_email ?? 'a blocked phone number'} · {row.attempts}{' '}
                        {row.attempts === 1 ? 'attempt' : 'attempts'}
                        {tried}
                      </div>
                      <div className="t-num" style={subStyle}>
                        Last {fmtDate(row.last_attempt_at)}, {fmtTime(row.last_attempt_at)} · expires{' '}
                        {fmtDate(row.expires_at)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={removingId === row.id}
                      onClick={() => void unblockIp(row)}
                    >
                      {removingId === row.id ? 'Removing...' : 'Unblock'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {ipAllow ? (
        <Card eyebrow="Never IP-blocked" title="Our Networks">
          <p style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Testing with a blocked email from these won't block the IP. Blocked emails are still refused.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--surface-raised)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
            }}
          >
            <span>
              You're on{' '}
              <span className="t-num" style={{ color: 'var(--text-body)' }}>
                {ipAllow.my_ip ?? 'an unknown IP'}
              </span>
              {ipAllow.my_ip_allowed ? ', which is never blocked.' : ', which can be blocked.'}
            </span>
            {!ipAllow.my_ip_allowed && ipAllow.my_ip ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={removingId === 'allow-me'}
                onClick={() => void allowMyIp()}
              >
                {removingId === 'allow-me' ? 'Adding...' : 'Never block this network'}
              </Button>
            ) : null}
          </div>
          {ipAllow.rows.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
              {ipAllow.rows.map((row, i) => (
                <div key={row.id} style={rowStyle(i)}>
                  <div style={{ flex: '1 1 0', minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                      {row.ip}
                      {row.is_mine ? (
                        <span style={{ color: 'var(--text-faint)', fontFamily: 'var(--font-ui)' }}> · you</span>
                      ) : null}
                    </div>
                    <div style={subStyle}>
                      {row.label ? `${row.label} · ` : ''}
                      <span className="t-num">added {fmtDate(row.created_at)}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={removingId === row.id}
                    onClick={() => void removeAllow(row)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}


// OAuth entry point is a plain navigation — keep it an anchor, dressed as a
// secondary button.
function ConnectLink({ label, icon }: { label: string; icon: string }) {
  return (
    <a
      // Direct function path, not the /api/* rewrite in netlify.toml. That
      // rewrite had exactly one consumer — this link — and it was falling
      // through to the SPA catch-all in public/_redirects, landing you on the
      // clients screen instead of Google's consent page. Every other function
      // in this app is called at /.netlify/functions/... for the same reason.
      href="/.netlify/functions/google-cal-auth?state=/bookings"
      className="hover-lift pressable"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        height: 'var(--control-sm)',
        padding: '0 10px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
        lineHeight: 1,
        color: 'var(--text-body)',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={icon} size={14} />
      {label}
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function AvailabilityPanel({
  eventTypes,
  savingId,
  onSaveStart,
  onSaveEnd,
  onReload,
}: {
  eventTypes: EventType[];
  savingId: string | null;
  onSaveStart: (id: string) => void;
  onSaveEnd: () => void;
  onReload: () => void;
}) {
  const toast = useToast();
  if (eventTypes.length === 0) {
    return (
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          padding: '56px 0',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <Icon name="calendar-days" size={24} style={{ color: 'var(--text-faint)' }} />
        <p style={{ margin: '12px 0 0', fontSize: 'var(--text-base)' }}>No event types yet</p>
      </div>
    );
  }
  return (
    <div
      className="ck-stagger"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: '880px' }}
    >
      {eventTypes.map((et) => (
        <EventTypeEditor
          key={et.id}
          eventType={et}
          isSaving={savingId === et.id}
          onSaveSettings={async (next) => {
            onSaveStart(et.id);
            try {
              const { error } = await supabase.rpc('booking_admin_update_event_type', {
                p_id: et.id,
                p_name: next.name,
                p_description: next.description ?? '',
                p_duration_minutes: next.duration_minutes,
                p_buffer_before_minutes: next.buffer_before_minutes,
                p_buffer_after_minutes: next.buffer_after_minutes,
                p_min_notice_minutes: next.min_notice_minutes,
                p_max_advance_days: next.max_advance_days,
                p_slot_increment_minutes: next.slot_increment_minutes,
                p_admin_timezone: next.admin_timezone,
                p_is_active: next.is_active,
                p_reminder_offset_minutes_1: next.reminder_offset_minutes_1 ?? undefined,
                p_reminder_offset_minutes_2: next.reminder_offset_minutes_2 ?? undefined,
              });
              if (error) throw error;
              onReload();
            } catch (e: unknown) {
              toast.error('Settings not saved', errMsg(e));
            } finally {
              onSaveEnd();
            }
          }}
          onSaveAvailability={async (next) => {
            onSaveStart(et.id);
            try {
              const { error } = await supabase.rpc('booking_admin_replace_availability', {
                p_event_type_id: et.id,
                p_rules: next.availability_rules.map((r) => ({
                  day_of_week: r.day_of_week,
                  start_minute: r.start_minute,
                  end_minute: r.end_minute,
                })),
              });
              if (error) throw error;
              onReload();
            } catch (e: unknown) {
              toast.error('Availability not saved', errMsg(e));
            } finally {
              onSaveEnd();
            }
          }}
          onSaveOverrides={async (next) => {
            onSaveStart(et.id);
            try {
              const { error } = await supabase.rpc('booking_admin_replace_overrides', {
                p_event_type_id: et.id,
                p_overrides: next.date_overrides.map((o) => ({
                  override_date: o.override_date,
                  start_minute: o.start_minute,
                  end_minute: o.end_minute,
                })),
              });
              if (error) throw error;
              onReload();
            } catch (e: unknown) {
              toast.error('Overrides not saved', errMsg(e));
            } finally {
              onSaveEnd();
            }
          }}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function EventTypeEditor({
  eventType,
  isSaving,
  onSaveSettings,
  onSaveAvailability,
  onSaveOverrides,
}: {
  eventType: EventType;
  isSaving: boolean;
  onSaveSettings: (next: EventType) => void;
  onSaveAvailability: (next: EventType) => void;
  onSaveOverrides: (next: EventType) => void;
}) {
  const [draft, setDraft] = useState<EventType>(eventType);
  const isDirtySettings = useMemo(() => {
    return (
      draft.name !== eventType.name ||
      (draft.description || '') !== (eventType.description || '') ||
      draft.duration_minutes !== eventType.duration_minutes ||
      draft.buffer_before_minutes !== eventType.buffer_before_minutes ||
      draft.buffer_after_minutes !== eventType.buffer_after_minutes ||
      draft.min_notice_minutes !== eventType.min_notice_minutes ||
      draft.max_advance_days !== eventType.max_advance_days ||
      draft.slot_increment_minutes !== eventType.slot_increment_minutes ||
      draft.admin_timezone !== eventType.admin_timezone ||
      draft.is_active !== eventType.is_active ||
      draft.reminder_offset_minutes_1 !== eventType.reminder_offset_minutes_1 ||
      draft.reminder_offset_minutes_2 !== eventType.reminder_offset_minutes_2
    );
  }, [draft, eventType]);

  const isDirtyAvail = useMemo(() => {
    return JSON.stringify(draft.availability_rules) !== JSON.stringify(eventType.availability_rules);
  }, [draft.availability_rules, eventType.availability_rules]);

  const isDirtyOver = useMemo(() => {
    return JSON.stringify(draft.date_overrides) !== JSON.stringify(eventType.date_overrides);
  }, [draft.date_overrides, eventType.date_overrides]);

  useEffect(() => {
    setDraft(eventType);
  }, [eventType]);

  const sectionHead: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)',
  };

  return (
    <Card
      eyebrow={draft.slug}
      title={draft.name}
      actions={
        <Switch
          checked={draft.is_active}
          onChange={(checked) => setDraft({ ...draft, is_active: checked })}
          label={
            <span style={{ fontSize: 'var(--text-sm)' }}>
              {draft.is_active ? 'Active' : 'Inactive'}
            </span>
          }
        />
      }
    >
      {/* Settings grid */}
      <div className="grid grid-cols-2 md:grid-cols-3" style={{ gap: 'var(--space-3)' }}>
        <div className="col-span-2 md:col-span-3">
          <Input
            label="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div className="col-span-2 md:col-span-3">
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Description
          </div>
          <textarea
            className="ck-textarea"
            rows={2}
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </div>
        <Input
          label="Duration · Min"
          type="number"
          value={draft.duration_minutes}
          onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) })}
        />
        <Input
          label="Slot Increment"
          type="number"
          value={draft.slot_increment_minutes}
          onChange={(e) => setDraft({ ...draft, slot_increment_minutes: Number(e.target.value) })}
        />
        <Input
          label="Min Notice · Min"
          type="number"
          value={draft.min_notice_minutes}
          onChange={(e) => setDraft({ ...draft, min_notice_minutes: Number(e.target.value) })}
        />
        <Input
          label="Max Advance · Days"
          type="number"
          value={draft.max_advance_days}
          onChange={(e) => setDraft({ ...draft, max_advance_days: Number(e.target.value) })}
        />
        <Input
          label="Buffer Before"
          type="number"
          value={draft.buffer_before_minutes}
          onChange={(e) => setDraft({ ...draft, buffer_before_minutes: Number(e.target.value) })}
        />
        <Input
          label="Buffer After"
          type="number"
          value={draft.buffer_after_minutes}
          onChange={(e) => setDraft({ ...draft, buffer_after_minutes: Number(e.target.value) })}
        />
        <div className="col-span-2 md:col-span-3">
          <Input
            label="Admin Timezone"
            value={draft.admin_timezone}
            onChange={(e) => setDraft({ ...draft, admin_timezone: e.target.value })}
            placeholder="America/Denver"
          />
        </div>
      </div>

      {/* Reminders */}
      <div
        style={{
          marginTop: 'var(--space-5)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border-hairline)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div className="t-eyebrow">Reminder Emails</div>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Customer-facing
          </span>
        </div>
        <p
          style={{
            margin: '6px 0 12px',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-faint)',
          }}
        >
          Sent automatically before the call. Switch a slot off to disable it.{' '}
          <span className="t-num">1440</span> = 24 hours · <span className="t-num">60</span> = 1
          hour · <span className="t-num">30</span> = 30 minutes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-3)' }}>
          <ReminderOffsetInput
            label="First Reminder · Min Before"
            value={draft.reminder_offset_minutes_1}
            placeholder="1440"
            onChange={(v) => setDraft({ ...draft, reminder_offset_minutes_1: v })}
          />
          <ReminderOffsetInput
            label="Second Reminder · Min Before"
            value={draft.reminder_offset_minutes_2}
            placeholder="60"
            onChange={(v) => setDraft({ ...draft, reminder_offset_minutes_2: v })}
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
        <Button size="sm" disabled={!isDirtySettings || isSaving} onClick={() => onSaveSettings(draft)}>
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Weekly Hours */}
      <div
        style={{
          marginTop: 'var(--space-5)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border-hairline)',
        }}
      >
        <div style={sectionHead}>
          <div>
            <div className="t-eyebrow">Weekly Hours</div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: '4px' }}>
              When invitees can book you
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={!isDirtyAvail || isSaving}
            onClick={() => onSaveAvailability(draft)}
          >
            {isSaving ? 'Saving...' : 'Save Availability'}
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
            const rules = draft.availability_rules.filter((r) => r.day_of_week === dow);
            const enabled = rules.length > 0;
            return (
              <div
                key={dow}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '9px 0',
                  borderBottom: '1px solid var(--border-hairline)',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ width: '110px', flexShrink: 0 }}>
                  <Switch
                    checked={enabled}
                    onChange={(checked) => {
                      if (checked) {
                        setDraft({
                          ...draft,
                          availability_rules: [
                            ...draft.availability_rules,
                            { day_of_week: dow, start_minute: 9 * 60, end_minute: 17 * 60 },
                          ],
                        });
                      } else {
                        setDraft({
                          ...draft,
                          availability_rules: draft.availability_rules.filter(
                            (r) => r.day_of_week !== dow
                          ),
                        });
                      }
                    }}
                    label={<span style={{ fontSize: 'var(--text-sm)' }}>{DAY_LABELS[dow]}</span>}
                  />
                </span>
                <span style={{ flex: 1 }} />
                {!enabled ? (
                  <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
                    Unavailable
                  </span>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      alignItems: 'flex-end',
                    }}
                  >
                    {rules.map((r, idx) => (
                      <RuleRow
                        key={idx}
                        rule={r}
                        onChange={(next) => {
                          const ruleIndex = draft.availability_rules.findIndex((x) => x === r);
                          const arr = [...draft.availability_rules];
                          arr[ruleIndex] = { ...next, day_of_week: dow };
                          setDraft({ ...draft, availability_rules: arr });
                        }}
                        onRemove={() => {
                          setDraft({
                            ...draft,
                            availability_rules: draft.availability_rules.filter((x) => x !== r),
                          });
                        }}
                      />
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="plus"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          availability_rules: [
                            ...draft.availability_rules,
                            { day_of_week: dow, start_minute: 9 * 60, end_minute: 12 * 60 },
                          ],
                        })
                      }
                    >
                      Add Window
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Date Overrides */}
      <div
        style={{
          marginTop: 'var(--space-5)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--border-hairline)',
        }}
      >
        <div style={sectionHead}>
          <div>
            <div className="t-eyebrow">Date Overrides</div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: '4px' }}>
              Block a holiday or replace hours on a date
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              size="sm"
              variant="ghost"
              icon="plus"
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setDraft({
                  ...draft,
                  date_overrides: [
                    ...draft.date_overrides,
                    { override_date: today, start_minute: null, end_minute: null },
                  ],
                });
              }}
            >
              Add Override
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!isDirtyOver || isSaving}
              onClick={() => onSaveOverrides(draft)}
            >
              {isSaving ? 'Saving...' : 'Save Overrides'}
            </Button>
          </div>
        </div>

        {draft.date_overrides.length === 0 ? (
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
              fontStyle: 'italic',
              padding: '8px 0',
            }}
          >
            No overrides
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {draft.date_overrides.map((o, idx) => (
              <OverrideRow
                key={idx}
                override={o}
                onChange={(next) => {
                  const arr = [...draft.date_overrides];
                  arr[idx] = next;
                  setDraft({ ...draft, date_overrides: arr });
                }}
                onRemove={() => {
                  setDraft({
                    ...draft,
                    date_overrides: draft.date_overrides.filter((_, i) => i !== idx),
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function RuleRow({
  rule,
  onChange,
  onRemove,
}: {
  rule: AvailabilityRule;
  onChange: (next: AvailabilityRule) => void;
  onRemove: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        type="time"
        className="ck-date-input"
        style={{ width: '110px' }}
        value={minutesToHHMM(rule.start_minute)}
        onChange={(e) => onChange({ ...rule, start_minute: hhmmToMinutes(e.target.value) })}
      />
      <span style={{ color: 'var(--text-faint)' }}>–</span>
      <input
        type="time"
        className="ck-date-input"
        style={{ width: '110px' }}
        value={minutesToHHMM(rule.end_minute)}
        onChange={(e) => onChange({ ...rule, end_minute: hhmmToMinutes(e.target.value) })}
      />
      <IconButton icon="x" size="sm" label="Remove Window" onClick={onRemove} />
    </div>
  );
}

function OverrideRow({
  override,
  onChange,
  onRemove,
}: {
  override: DateOverride;
  onChange: (next: DateOverride) => void;
  onRemove: () => void;
}) {
  const fullDay = override.start_minute == null && override.end_minute == null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '9px 0',
        borderBottom: '1px solid var(--border-hairline)',
        flexWrap: 'wrap',
      }}
    >
      <input
        type="date"
        className="ck-date-input"
        style={{ width: '150px' }}
        value={override.override_date}
        onChange={(e) => onChange({ ...override, override_date: e.target.value })}
      />
      {/* Phones: date + delete on the first line, the hours below (order-1);
          desktop keeps one row in source order. */}
      <span className="order-1 md:order-none">
        <Checkbox
          checked={fullDay}
          onChange={(checked) => {
            if (checked) {
              onChange({ ...override, start_minute: null, end_minute: null });
            } else {
              onChange({ ...override, start_minute: 9 * 60, end_minute: 17 * 60 });
            }
          }}
          label={<span style={{ fontSize: 'var(--text-sm)' }}>Block Full Day</span>}
        />
      </span>
      {!fullDay && (
        <span className="order-1 md:order-none" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="time"
            className="ck-date-input"
            style={{ width: '110px' }}
            value={minutesToHHMM(override.start_minute || 0)}
            onChange={(e) => onChange({ ...override, start_minute: hhmmToMinutes(e.target.value) })}
          />
          <span style={{ color: 'var(--text-faint)' }}>–</span>
          <input
            type="time"
            className="ck-date-input"
            style={{ width: '110px' }}
            value={minutesToHHMM(override.end_minute || 0)}
            onChange={(e) => onChange({ ...override, end_minute: hhmmToMinutes(e.target.value) })}
          />
        </span>
      )}
      <span style={{ flex: 1 }} />
      <IconButton icon="trash-2" size="sm" label="Remove Override" onClick={onRemove} />
    </div>
  );
}

// Reminder offset input — a number input with an on/off switch. null = off.
function ReminderOffsetInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: number | null;
  placeholder: string;
  onChange: (v: number | null) => void;
}) {
  const enabled = value !== null;
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1, opacity: enabled ? 1 : 0.45 }}>
          <Input
            type="number"
            min={1}
            disabled={!enabled}
            value={enabled ? value : ''}
            placeholder={placeholder}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n) || n <= 0) return;
              onChange(n);
            }}
          />
        </div>
        <Switch
          checked={enabled}
          onChange={(checked) => onChange(checked ? Number(placeholder) || 60 : null)}
          label={
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {enabled ? 'On' : 'Off'}
            </span>
          }
        />
      </div>
    </div>
  );
}

// Test-send — calls /api/booking-reminder-test on bullfinch.io. The site URL
// is configurable via VITE_BULLFINCH_SITE_URL for staging swaps.
function ReminderTestRow() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  async function sendTest() {
    setStatus(null);
    setSending(true);
    try {
      const siteUrl =
        (import.meta.env.VITE_BULLFINCH_SITE_URL as string | undefined) || 'https://bullfinch.io';
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Not signed in.');
      const res = await fetch(`${siteUrl.replace(/\/$/, '')}/api/booking-reminder-test`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ to: email }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setStatus({ kind: 'ok', msg: `Sent to ${email}. Check your inbox.` });
    } catch (e: unknown) {
      setStatus({ kind: 'err', msg: errMsg(e) || 'Send failed.' });
    } finally {
      setSending(false);
    }
  }

  const sent = status?.kind === 'ok';

  return (
    <div>
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}
      >
        Renders the reminder email against placeholder booking details and sends it to the address
        below.
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          type="email"
          fieldSize="sm"
          icon="mail"
          placeholder="kyle@bullfinch.io"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setStatus(null);
          }}
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={sending || !email.trim() || sent}
          onClick={() => void sendTest()}
        >
          {sending ? 'Sending...' : sent ? 'Sent' : 'Send Test'}
        </Button>
      </div>
      {status && (
        <div
          style={{
            marginTop: '10px',
            fontSize: 'var(--text-xs)',
            color: status.kind === 'ok' ? 'var(--status-up)' : 'var(--status-down)',
          }}
        >
          {status.msg}
        </div>
      )}
    </div>
  );
}
